// What a HubSpot call is allowed to say on the Activity page — the ledger
// row (GET /api/hubspot/metrics rows.calls) and the whole record
// (GET /api/hubspot/call?id=). Pure: no React, no network, node-tested in
// scripts/test-callContent.mjs.
//
// THE FACTS THIS ENCODES (verified against the live portal, 2026-09-10):
//   * HubSpot stores a call's outcome as an opaque id on this portal
//     (b2cf5968-…). The mirror translates it through the portal's own
//     outcome list and sends the word as `outcome`; `disposition` stays the
//     raw value. A row with neither is a call HubSpot holds no outcome for,
//     and the page says so — it never prints "no answer" over a blank.
//   * `pickup` is the mirror's verdict on whether anyone answered —
//     picked_up / not_picked_up — derived from the outcome, else the dial
//     status, else an inbound call's answer disposition, never from the
//     duration (which counts the ringing and a voicemail too). Absent means
//     unknown, and unknown is rendered as unknown.
//   * `source` is VOIP for a call placed or taken on HubSpot's own phone
//     number, which is when HubSpot records the call itself: the numbers,
//     a recording, a transcript and an AI-written summary. A row with no
//     source was logged by hand and carries only the rep's typed notes.
//   * `content` lists which of those records the mirror holds, richest
//     first: transcript, summary, recording, voicemail, notes.

// Which channel the call went through, in words. Absent = the rep logged
// it by hand in HubSpot after the fact.
export const CALL_SOURCE_LABELS = {
  VOIP: 'HubSpot phone',
  ZOOM: 'Zoom',
  INTEGRATIONS_PLATFORM: 'Integration',
  HUBSPOT_MEETINGS: 'HubSpot meeting',
  IN_PERSON_MEETINGS: 'In person',
}
export const HAND_LOGGED_LABEL = 'Logged by hand'

// 'LEFT_VOICEMAIL' → 'Left voicemail'. Local rather than hubspot.js's
// humanizeEnum so this module stays importable in node without the Cognito
// client that hubspot.js pulls in.
function wordsOf(value) {
  const text = String(value || '')
    .trim()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
  if (!text) return ''
  const lower = text === text.toUpperCase() ? text.toLowerCase() : text
  return lower.charAt(0).toUpperCase() + lower.slice(1)
}

export function callSourceLabel(row) {
  const raw = String((row && row.source) || '')
    .trim()
    .toUpperCase()
  if (!raw) return HAND_LOGGED_LABEL
  return CALL_SOURCE_LABELS[raw] || wordsOf(raw)
}

export function isHubSpotPhoneCall(row) {
  return (
    String((row && row.source) || '')
      .trim()
      .toUpperCase() === 'VOIP'
  )
}

const OPAQUE_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-/i

// { label, opaque, raw } or null when HubSpot recorded no outcome at all.
// `opaque` is an id the mirror could not translate: shown AS an id, never
// dressed up as a word.
export function callOutcome(row) {
  const outcome = String((row && row.outcome) || '').trim()
  const raw = String((row && row.disposition) || '').trim()
  if (outcome) return { label: outcome, opaque: false, raw }
  if (!raw) return null
  if (OPAQUE_ID_RE.test(raw)) return { label: `id ${raw.slice(0, 8)}`, opaque: true, raw }
  return { label: wordsOf(raw), opaque: false, raw }
}

export const PICKUP_LABELS = { picked_up: 'Picked up', not_picked_up: 'Not picked up' }
export const PICKUP_TONES = { picked_up: 'success', not_picked_up: 'warning' }

// { key, label, tone } or null when the mirror holds no verdict.
export function callPickup(row) {
  const key = String((row && row.pickup) || '').trim()
  if (!PICKUP_LABELS[key]) return null
  return { key, label: PICKUP_LABELS[key], tone: PICKUP_TONES[key] }
}

// The records HubSpot itself keeps for a call, in the mirror's fixed order.
export const CONTENT_LABELS = {
  transcript: 'Transcript',
  summary: 'AI summary',
  recording: 'Recording',
  voicemail: 'Voicemail',
  notes: 'Notes',
}
const CONTENT_ORDER = Object.keys(CONTENT_LABELS)

// ['transcript','summary'] → [{ key, label }], unknown keys dropped, order
// fixed regardless of the order the payload used.
export function callContentLabels(content) {
  const have = new Set(Array.isArray(content) ? content.map((c) => String(c || '').trim()) : [])
  return CONTENT_ORDER.filter((k) => have.has(k)).map((k) => ({ key: k, label: CONTENT_LABELS[k] }))
}

// A call's length the way a rep reads a call log — '43s', '2m 5s', '15m' —
// or null when HubSpot never recorded one. Absent/unusable is null and gets
// the dash; a duration that really is 0 is a real zero-length call and
// prints as 0s, which is a different claim from "we were not told".
export function formatDurationMs(ms) {
  if (ms == null || ms === '') return null
  const n = Number(ms)
  if (!Number.isFinite(n) || n < 0) return null
  const secs = Math.round(n / 1000)
  if (secs < 60) return `${secs}s`
  const mins = Math.floor(secs / 60)
  const rest = secs % 60
  return rest ? `${mins}m ${rest}s` : `${mins}m`
}

// '+13465665750' → '(346) 566-5750'; anything not a plain 10/11-digit North
// American number is returned as typed.
export function formatPhone(value) {
  const raw = String(value || '').trim()
  if (!raw) return ''
  const digits = raw.replace(/\D/g, '')
  const ten = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits.length === 10 ? digits : ''
  if (!ten) return raw
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`
}

const SUMMARY_KINDS = new Set(['p', 'li', 'sub'])

// The whole record from GET /call?id=, made safe to render: every field
// present with its type, the summary reduced to the {heading, blocks} shape
// the page draws as text. Unknown block kinds become paragraphs rather than
// vanishing — a sentence the mirror sent is never dropped over a label.
export function normalizeCallRecord(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const str = (v) => (v == null ? '' : String(v))
  const num = (v) => {
    if (v == null || v === '') return null
    const n = Number(v)
    return Number.isFinite(n) ? n : null
  }
  const sections = []
  for (const s of Array.isArray(raw.summary) ? raw.summary : []) {
    if (!s || typeof s !== 'object') continue
    const blocks = []
    for (const b of Array.isArray(s.blocks) ? s.blocks : []) {
      const text = str(b && b.text).trim()
      if (!text) continue
      const kind = SUMMARY_KINDS.has(b.kind) ? b.kind : 'p'
      blocks.push({ kind, text })
    }
    const heading = str(s.heading).trim()
    if (heading || blocks.length) sections.push({ heading, blocks })
  }
  const person = (v) => (v && typeof v === 'object' ? { id: str(v.id), name: str(v.name), email: str(v.email), phone: str(v.phone), jobTitle: str(v.job_title), hubspotUrl: str(v.hubspot_url) } : null)
  const company = raw.company && typeof raw.company === 'object' ? { id: str(raw.company.id), name: str(raw.company.name), domain: str(raw.company.domain), hubspotUrl: str(raw.company.hubspot_url) } : null
  return {
    id: str(raw.id),
    at: str(raw.at),
    day: str(raw.day),
    title: str(raw.title),
    status: str(raw.status),
    direction: str(raw.direction),
    disposition: str(raw.disposition),
    outcome: str(raw.outcome),
    pickup: str(raw.pickup),
    durationMs: num(raw.duration_ms),
    recordingDurationMs: num(raw.recording_duration_ms),
    source: str(raw.source),
    fromNumber: str(raw.from_number),
    toNumber: str(raw.to_number),
    notes: str(raw.notes),
    summary: sections,
    summaryText: str(raw.summary_text),
    // HubSpot's own flag that IT transcribed the call — not text anyone can
    // read. The text is `transcript`, made by the mirror from the recording.
    hubspotHasTranscript: raw.hubspot_has_transcript === true || raw.has_transcript === true,
    transcript: normalizeTranscript(raw.transcript),
    hasRecording: raw.has_recording === true,
    hasVoicemail: raw.has_voicemail === true,
    content: Array.isArray(raw.content) ? raw.content.map((c) => str(c)) : [],
    owner: raw.owner && typeof raw.owner === 'object' ? { id: str(raw.owner.id), name: str(raw.owner.name), email: str(raw.owner.email) } : null,
    company,
    companyIds: Array.isArray(raw.company_ids) ? raw.company_ids.map((c) => str(c)) : [],
    contact: person(raw.contact),
    hubspotUrl: str(raw.hubspot_url),
    reviewUrl: str(raw.review_url),
    updatedAt: str(raw.updated_at),
    syncedAt: str(raw.synced_at),
  }
}

// ── the transcript (GET /call → call.transcript) ─────────────────────────────
// HubSpot exposes no API for its own transcripts, so the mirror transcribes
// HubSpot's recording itself: one party per recording channel, so every line
// names its speaker. status: ready | pending (a recording waits in the queue)
// | failed (reason says why) | none (no recording — logged by hand) |
// unreadable (done, but the text could not be read just now). null when the
// mirror is a deploy behind and sends no transcript block at all.
export const TRANSCRIPT_STATUSES = ['ready', 'pending', 'failed', 'none', 'unreadable']

export function normalizeTranscript(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const str = (v) => (v == null ? '' : String(v))
  const num = (v) => (v == null || v === '' || !Number.isFinite(Number(v)) ? null : Number(v))
  const speakers = raw.speakers && typeof raw.speakers === 'object' ? raw.speakers : {}
  const segments = []
  for (const seg of Array.isArray(raw.segments) ? raw.segments : []) {
    if (!seg || typeof seg !== 'object') continue
    const text = str(seg.text).trim()
    if (!text) continue
    segments.push({
      start: Math.max(0, num(seg.start) || 0),
      end: Math.max(0, num(seg.end) || 0),
      speaker: seg.speaker === 'rep' || seg.speaker === 'contact' ? seg.speaker : 'unknown',
      text,
    })
  }
  return {
    status: TRANSCRIPT_STATUSES.includes(raw.status) ? raw.status : 'unreadable',
    reason: str(raw.reason),
    speakers: {
      rep: str(speakers.rep) || 'Rep',
      contact: str(speakers.contact) || 'Contact',
      unknown: str(speakers.unknown) || 'Speaker',
    },
    segments,
    text: str(raw.text),
    language: str(raw.language),
    durationS: num(raw.duration_s),
    model: str(raw.model),
    channels: num(raw.channels),
    speakerRule: str(raw.speaker_rule),
    truncated: raw.truncated === true,
    at: str(raw.at),
  }
}

export function speakerName(transcript, key) {
  const names = (transcript && transcript.speakers) || {}
  return names[key] || names.unknown || 'Speaker'
}

// 42.6 → '0:42', 3725 → '1:02:05'
export function formatClock(seconds) {
  const n = Math.max(0, Math.floor(Number(seconds) || 0))
  const h = Math.floor(n / 3600)
  const m = Math.floor((n % 3600) / 60)
  const sec = String(n % 60).padStart(2, '0')
  return h ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`
}

// What Copy transcript puts on the clipboard: one line per segment, with the
// speakers' real names rather than the stored roles.
export function transcriptPlainText(transcript) {
  if (!transcript || !transcript.segments || !transcript.segments.length) return ''
  return transcript.segments.map((seg) => `[${formatClock(seg.start)}] ${speakerName(transcript, seg.speaker)}: ${seg.text}`).join('\n')
}

// The ledger row's word for a transcript that is not readable yet; null when
// it is ready (the Transcript content chip says so) or there is no recording.
export function transcriptStatusChip(row) {
  const status = String((row && row.transcript_status) || '')
  if (status === 'pending') {
    return { key: 'pending', label: 'Transcribing…', tone: 'neutral', title: 'HubSpot kept a recording of this call; the mirror is transcribing it and the transcript appears when it lands.' }
  }
  if (status === 'failed') {
    return { key: 'failed', label: 'Transcript failed', tone: 'warning', title: 'The mirror could not transcribe this recording. Open the call for the reason.' }
  }
  return null
}

// One line that says what happened on the call, for a timeline block's
// caption and the call page's header: "Left voicemail · Not picked up · 43s".
export function callHeadline(row) {
  const parts = []
  const outcome = callOutcome(row)
  if (outcome) parts.push(outcome.label)
  const pickup = callPickup(row)
  if (pickup && (!outcome || outcome.opaque)) parts.push(pickup.label)
  const duration = formatDurationMs(row && (row.duration_ms != null ? row.duration_ms : row.durationMs))
  if (duration != null) parts.push(duration)
  return parts.join(' · ')
}
